import { useEffect, useRef, useState } from 'react';

import { useWindowSize } from './useWindowSize';

type EllipsisMode = 'middle' | 'end';

const getTextWidth = (element: HTMLElement): number => element.getBoundingClientRect().width;

export const useFitTextEllipsis = (text: string, maxWidthPx: number, mode: EllipsisMode = 'middle', prefixLength = 4) => {
  const measureRef = useRef<HTMLSpanElement>(null);
  const [displayText, setDisplayText] = useState('');
  const [letterSpacing, setLetterSpacing] = useState(0);
  const [windowWidth] = useWindowSize();

  useEffect(() => {
    const span = measureRef.current;

    if (!span || !text) {
      setDisplayText(text || '');
      setLetterSpacing(0);
      return;
    }

    const measure = () => {
      span.style.letterSpacing = '0px';
      span.textContent = text;

      if (getTextWidth(span) <= maxWidthPx) {
        setDisplayText(text);
        calcLetterSpacing(span, text);
        return;
      }

      const ellipsis = '...';

      if (mode === 'end') {
        let result = ellipsis;

        for (let n = 1; n <= text.length; n++) {
          const candidate = text.slice(0, n) + ellipsis;

          span.textContent = candidate;

          if (getTextWidth(span) > maxWidthPx) {
            break;
          }

          result = candidate;
        }

        setDisplayText(result);
        calcLetterSpacing(span, result);
      } else {
        const prefix = text.slice(0, prefixLength);
        let result = prefix + ellipsis;

        for (let n = 1; n <= text.length - prefixLength; n++) {
          const candidate = prefix + ellipsis + text.slice(-n);

          span.textContent = candidate;

          if (getTextWidth(span) > maxWidthPx) {
            break;
          }

          result = candidate;
        }

        setDisplayText(result);
        calcLetterSpacing(span, result);
      }

      function calcLetterSpacing(span: HTMLSpanElement, finalText: string) {
        span.textContent = finalText;
        const textWidth = getTextWidth(span);
        const charCount = finalText.length;

        if (charCount <= 1 || textWidth >= maxWidthPx) {
          setLetterSpacing(0);
          return;
        }

        setLetterSpacing((maxWidthPx - textWidth) / (charCount - 1));
      }
    };

    document.fonts.ready.then(measure);
  }, [text, maxWidthPx, mode, prefixLength, windowWidth]);

  return { displayText, measureRef, letterSpacing };
};
